/**
 * FeatureGate Component
 *
 * Schuetzt Routen basierend auf Feature-Flags.
 * Zeigt einen Hinweis für Beta-Features oder verweigerten Zugang.
 */

import { ReactNode, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, FlaskConical, Lock } from 'lucide-react';

interface FeatureGateProps {
  featureKey: string;
  children: ReactNode;
  fallbackPath?: string;
}

export function FeatureGate({
  featureKey,
  children,
  fallbackPath = '/dashboard',
}: FeatureGateProps) {
  const { user, isAuthenticated, originalRole } = useAuth();
  const { hasAccess, isLoading, isBetaFeature, reason } = useFeatureAccess(featureKey);

  // Merkt sich, ob schon einmal Zugriff gewährt wurde. Periodische
  // Auth-Refreshes (60s-Timer, Fokus-Handler) invalidieren den
  // Feature-Access-Cache und lösen einen Background-Refetch aus -- ohne
  // dieses Flag würde isLoading kurz wieder true und die Kinder (z.B. eine
  // gerade laufende Zeichnung im Plan-Trade-Chart) würden unmounted.
  const hadAccessRef = useRef(false);
  if (hasAccess) {
    hadAccessRef.current = true;
  }

  // Nicht eingeloggt -> durchlassen (SEO, öffentliche Vorschau)
  // Feature-Gate greift nur für authentifizierte User
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Admins haben immer Zugang (aber NICHT im Demo-Modus - da entscheidet Feature-Access)
  const isDemoActive = originalRole === 'admin' && user?.role !== 'admin';
  if (user?.role === 'admin' && !isDemoActive) {
    return <>{children}</>;
  }

  // Ladeindikator -- nur beim allerersten Laden, nicht bei Background-Refetches
  if (isLoading && !hadAccessRef.current) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  // Kein Zugang (aber: war schon mal gewährt -> Background-Refetch, nicht neu bewerten)
  if (!hasAccess && !hadAccessRef.current) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        {isBetaFeature ? (
          <>
            <div className="w-20 h-20 rounded-full bg-sky-500/10 flex items-center justify-center mb-6">
              <FlaskConical className="w-10 h-10 text-sky-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Beta-Feature
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mb-4">
              Dieses Feature ist derzeit nur für Beta-Tester verfügbar.
            </p>
            {reason && (
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {reason}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-gray-500/10 flex items-center justify-center mb-6">
              <Lock className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Zugriff verweigert
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md">
              {reason || 'Sie haben keinen Zugriff auf diese Funktion.'}
            </p>
          </>
        )}
      </div>
    );
  }

  // Zugang gewaehrt
  return <>{children}</>;
}

export default FeatureGate;
